import { all, call, fork, put, takeLatest, debounce, select, race, delay } from 'redux-saga/effects';
import i18n from 'i18next';
import _, { forEach } from 'lodash';
import moment from 'moment';
import {
  FETCH_ALERTS_BY_LOCATION, FETCH_ALERTS_BY_USER, CLEAR_ALERT, CLEAR_BULK_ALERTS, FETCH_ALARM_LIST, FETCH_ALERT_EXPORT_CSV_DATA, CLEAR_CRITICAL_ALERT
} from '../constants/ActionTypes';
import { showError, showMessage, clearMessage } from '../actions/Common';
import { CustomAction, ListView, ListViewRow } from 'model/state';
import {
  fetchAlertsByLocationSuccess, fetchAlertsByLocationError, AlertPayload, fetchAlertsByUserIdSuccess,
  fetchAlertsByUserIdError, clearAlertSuccess, clearAlertError, clearBulkAlertsError, clearBulkAlertsSuccess,
  fetchAlarmListError, fetchAlarmListSuccess, fetchAlertExportCsvDataError, fetchAlertExportCsvDataSuccess
} from 'actions';
import { getAlerts, postAction, postAlertFeedback } from 'api/Alerts';
import {
  AlertsResponse, toAlert, fromSeverity, fromStatus, GetLocationsResponse, toAlertsWithLocationData, AlarmsResponse,
  toAlarmLabelList, generateCsvFromAlerts, alertFeedbackTypeToAlertPayload
} from 'model/api';
import { AlertTotals, MessageType, Alert, User, Device, AlarmLabel } from 'model';
import { selectAlarmList, selectLoggedUser, selectSelectedAlertsToResolve } from 'selectors';
import { getLocations } from 'api/Location';
import { getDevice } from 'api/Device';
import { isEmpty } from 'lodash';
import { getAlarms } from "../api/Alarm";
import { analytics } from 'utils/Analytics';

function* fetchAlertsByLocationWorker(action: CustomAction<AlertPayload>) {
  const locationId = action.payload?.locationId;
  try {
    if (!locationId) {
      throw Error('Invalid Parameters');
    }
    const [allAlerts, pendingAlerts]: AlertsResponse[] = yield all([
      call(getAlerts, locationId),
      call(getAlerts, locationId, undefined, [], 'triggered', undefined, 1, 50),
    ]);

    yield put(fetchAlertsByLocationSuccess(
      allAlerts.items.map(alert => toAlert(alert)),
      pendingAlerts.items.map(alert => toAlert(alert))
    ));
  } catch (err) {
    yield put(fetchAlertsByLocationError());
    yield put(showError(i18n.t('connection_error'), err));
  }
}

function* fetchAlertsByUserWorker(action: CustomAction<AlertPayload>) {
  const userId = action.payload?.userId;
  const filter = action.payload?.filter;
  try {
    if (!userId || !filter) {
      throw Error('Invalid Parameters');
    }

    const alarmList: ListView<AlarmLabel> = yield select(selectAlarmList());
    const alarmIds = filter.alarmNames?.reduce((prev: number[], curr) => [
      ...prev,
      ...(alarmList.rows?.find(a => a.name === curr)?.ids || [])
    ], []) || [];

    const fromDate = moment().subtract(2, 'months').toISOString();
    const createdAtFilter = `gt:${fromDate}`;

    const alertPromise = async (locationIds: string[] | undefined) => {
      return getAlerts(
        locationIds,
        userId,
        filter.severity !== undefined ? [fromSeverity(filter.severity)] : [],
        filter.status !== undefined ? fromStatus(filter.status) : undefined,
        alarmIds,
        filter.page !== undefined ? filter.page : undefined,
        filter.size !== undefined ? filter.size : undefined,
        createdAtFilter
      )
    }

    //used for getting active alerts only
    const alertTriggeredPromise = async (locationIds: string[] | undefined) => {
      return getAlerts(
        locationIds,
        userId,
        filter.severity !== undefined ? [fromSeverity(filter.severity)] : [],
        'triggered',
        alarmIds,
        filter.page,
        filter.size,
        createdAtFilter,
        filter.severity === undefined && filter.status === 1, //Set to true when Log filter tab is selected. So that getAlerts will not get any alert history to display
      )
    }
    let locationIds: string[] | undefined;
    if (!isEmpty(filter.search)) {
      const locationSearchResponse: GetLocationsResponse = yield call(getLocations, { userId, q: filter.search, page: 1, size: 15 });
      locationIds = locationSearchResponse.items.map(l => l.id);
    } else if (filter.locationIds?.length) {
      locationIds = filter.locationIds;
    } 
    const [alerts, totals]: [AlertsResponse, AlertTotals | undefined] = yield all([
      call(filter.activeOnly ? alertTriggeredPromise : alertPromise, locationIds),
      call(fetchAlertTotals, userId, locationIds, filter.page || 1, alarmIds, createdAtFilter, filter.activeOnly),
    ]);

    const calculatedTotals = totals ? {
      ...totals,
      logs: totals.all - totals.critical - totals.warning,
    } : undefined;

    yield put(fetchAlertsByUserIdSuccess({
        alerts: toAlertsWithLocationData(alerts.items, userId),
        total: alerts.total,
        page: filter.page !== undefined ? filter.page : 1
      },
      calculatedTotals
    ));
  } catch (err) {
    yield put(fetchAlertsByUserIdError());
    yield put(showError(i18n.t('connection_error'), err));
  }
}

function* fetchAlertTotals(
    userId: string,
    locationIds: string[] | undefined,
    page: number,
    alarmIds: number[] | undefined,
    createdAtFilter: string | undefined,
    activeAlertsOnly?: boolean,
  ): any {

  let totals: AlertTotals = {
    all: 0,
    critical: 0,
    warning: 0,
    logs: 0,
  };

  if (page > 1) {
    return undefined;
  }
  if (activeAlertsOnly) {
    const [allData, criticalData, warningData] = yield all ([
      call(getAlerts, locationIds, userId, [], 'triggered', alarmIds, 1, 1, createdAtFilter),
      call(getAlerts, locationIds, userId, ['critical'], 'triggered', alarmIds, 1, 1, createdAtFilter),
      call(getAlerts, locationIds, userId, ['warning'], 'triggered', alarmIds, 1, 1, createdAtFilter),
    ]);
    totals = {
      ...totals,
      all: allData.total,
      critical: criticalData.total,
      warning: warningData.total,
    };
  } else {
    const [allData, criticalData, warningData] = yield all([
      call(getAlerts, locationIds, userId, [], undefined, alarmIds, 1, 1, createdAtFilter),
      call(getAlerts, locationIds, userId, ['critical'], 'triggered', alarmIds, 1, 1, createdAtFilter),
      call(getAlerts, locationIds, userId, ['warning'], 'triggered', alarmIds, 1, 1, createdAtFilter),
    ]);
    totals = {
      ...totals,
      all: allData.total,
      critical: criticalData.total,
      warning: warningData.total,
    };
  }

  return totals;
}

function* clearAlertWorker(action: CustomAction<AlertPayload>) {
  const alert = action.payload?.alert;
  const snoozeSeconds = action.payload?.snoozeSeconds || 0;
  try {
    if (alert === undefined) {
      throw Error('Invalid Parameters');
    }
    yield put(clearMessage());
    yield call(postAction, {
      alarmIds: [alert.alarm.id],
      snoozeSeconds,
      deviceId: alert.deviceId
    });

    yield put(clearAlertSuccess(alert));
    yield put(showMessage({
      message: i18n.t(`alert-${snoozeSeconds > 0 ? 'snoozed': 'cleared'}-title`),
      description: i18n.t(`alert-${snoozeSeconds > 0 ? 'snoozed': 'cleared'}-description`),
      type: MessageType.SUCCESS
    }));
  } catch (err) {
    yield put(showError(i18n.t('connection_error'), err));
    yield put(clearAlertError());
  }
}

function* clearCriticalAlertWorker(action: CustomAction<AlertPayload>) {
  const alert = action.payload?.alert; 
  const snoozeSeconds = action.payload?.snoozeSeconds || 0; 
  const feedbackType = action.payload?.feedbackType;
  try {
    if (alert === undefined || feedbackType === undefined) { 
      throw Error('Invalid Parameters'); 
    }
    yield put(clearMessage()); 
    yield call(postAlertFeedback, { 
      alertId: alert.id,
      apiPayload: {feedback: alertFeedbackTypeToAlertPayload(feedbackType)} 
    });
    yield call (postAction, { 
      alarmIds: [alert.alarm.id], 
      snoozeSeconds,
      deviceId: alert.deviceId
    });
    yield put(clearAlertSuccess(alert)); 
    yield put(showMessage({
      message: i18n.t(`alert-feedback-submitted-title`),
      description: i18n.t(`alert-feedback-submitted-description`),
      type: MessageType.SUCCESS
    }));

    const loggedUser: User = yield select(selectLoggedUser());
    analytics.trackCriticalResolve(loggedUser?.id || '');
  } catch(err) {
    yield put(showError(i18n.t('connection_error'), err));
    yield put(clearAlertError());
  }
}

function* clearBulkAlertsWorker() {
  try {
    const selected: Array<ListViewRow<Alert>> = yield select(selectSelectedAlertsToResolve());
    const loggedUser: User = yield select(selectLoggedUser());

    const callEffects = [];
    for (let index = 0; index < selected.length; index++) {
      callEffects.push(call(postAction, {
        alarmIds: [selected[index].alarm.id],
        snoozeSeconds: 0, // just clear the alert for now
        deviceId: selected[index].deviceId
      }))
    }
    yield all(callEffects);
    forEach(selected, (alert) => {
      if (alert.alarm.severity === 0) {
        analytics.trackCriticalResolve(loggedUser?.id || '');
      };
    });

    yield put(clearBulkAlertsSuccess(selected));
    yield put(showMessage({ message: i18n.t('bulk-alert-cleared-title'), description: i18n.t('bulk-alert-cleared-description'), type: MessageType.SUCCESS }));
  } catch (err) {
    yield put(showError(i18n.t('connection_error'), err));
    yield put(clearBulkAlertsError());
  }
}

function* fetchAlarmListWorker() {
  try {
    const loggedUser: User | undefined = yield select(selectLoggedUser());

    const alarmListResponse: AlarmsResponse = yield call(getAlarms, loggedUser?.locale);
    const alarmList = toAlarmLabelList(alarmListResponse);

    yield put(fetchAlarmListSuccess(alarmList));
  } catch (err) {
    yield put(showError(i18n.t('connection_error'), err));
    yield put(fetchAlarmListError());
  }
};

export function* fetchAlertCsvDataWorker(action: CustomAction<AlertPayload>) {
  const userId = action.payload?.userId;
  const filter = action.payload?.filter;
  const alarmList: ListView<AlarmLabel> = yield select(selectAlarmList());
  const alarmIds = filter?.alarmNames?.reduce((prev: number[], curr) => [
    ...prev,
    ...(alarmList.rows?.find(a => a.name === curr)?.ids || [])
  ], []) || [];

  try {
    if (!userId || !filter) {
      throw Error('Invalid Parameters');
    }

    const alertPromise = async (locationIds: string[] | undefined) => {
      return getAlerts(
        locationIds,
        userId,
        filter.severity !== undefined ? [fromSeverity(filter.severity)] : [],
        filter.status !== undefined ? fromStatus(filter.status) : undefined,
        alarmIds,
        1, //page
        1000, // size currently supported 1000 row limit
      )
    };

    const emptyPromise = async () => ({ items: [], total: 0 });

    let locationIds: string[] | undefined;
    if (!isEmpty(filter.search)) {
      const locationSearchResponse: GetLocationsResponse = yield call(getLocations, { userId, q: filter.search, page: 1, size: 15 });
      locationIds = locationSearchResponse.items.map(l => l.id);
    } else if (filter.locationIds?.length) {
      locationIds = filter.locationIds;
    }

    const result = yield race({
      alerts: call(locationIds && locationIds.length === 0 ? emptyPromise : alertPromise, locationIds),
      timeout: delay(300000),
    });

    if (result.alerts) {
      const alerts: AlertsResponse = result.alerts;
      const deviceIds = _.uniq(alerts.items.map(x => x.deviceId));
      const devices = yield all(deviceIds.map(deviceId => call(getDevice, deviceId)));
      const deviceMap = _.keyBy<Device>(devices, 'id');

      const csvData = generateCsvFromAlerts(alerts.items, deviceMap);

      yield put(fetchAlertExportCsvDataSuccess(csvData));
    } else {
      yield put(fetchAlertExportCsvDataError());
      yield put(showError(i18n.t('timeout_error')));
    }
  } catch (err) {
    yield put(fetchAlertExportCsvDataError());
    yield put(showError(i18n.t('connection_error'), err));
  }
}

export function* fetchAlertsByLocationSaga() {
  yield takeLatest(FETCH_ALERTS_BY_LOCATION, fetchAlertsByLocationWorker);
}

export function* fetchAlertsByUserSaga() {
  yield debounce(500, FETCH_ALERTS_BY_USER, fetchAlertsByUserWorker);
}

export function* clearAlertSaga() {
  yield takeLatest(CLEAR_ALERT, clearAlertWorker);
}

export function* clearCriticalAlertFeedbackSaga() {
  yield takeLatest(CLEAR_CRITICAL_ALERT, clearCriticalAlertWorker)
}

export function* clearBulkAlertsSaga() {
  yield takeLatest(CLEAR_BULK_ALERTS, clearBulkAlertsWorker);
}

export function* fetchAlarmListSaga() {
  yield takeLatest(FETCH_ALARM_LIST, fetchAlarmListWorker);
};

export function* fetchAlertCsvDataSaga() {
  yield takeLatest(FETCH_ALERT_EXPORT_CSV_DATA, fetchAlertCsvDataWorker);
}

export default function* alertsSaga() {
  yield all([
    fork(fetchAlertsByLocationSaga),
    fork(fetchAlertsByUserSaga),
    fork(clearAlertSaga),
    fork(clearCriticalAlertFeedbackSaga),
    fork(clearBulkAlertsSaga),
    fork(fetchAlarmListSaga),
    fork(fetchAlertCsvDataSaga),
  ]);
}